/**
 * Canonical frame <-> real-video-time mapping (single source since 2026-08-24
 * — previously duplicated in gms/matchsummary/frameTime.ts and inline in
 * pages/playlists/PlaylistPlayer.tsx). Ported verbatim from
 * app/templates/game_match_summary/index.html (~lines 2055-2095), itself a
 * mirror of GameFile.frame_to_video_time / video_time_to_frame.
 *
 * KNOWN DIVERGENCE from the Python model (documented by test; which side is
 * canonical is a founder decision, ledgered in docs/follow-ups.md):
 * GameFile.video_time_to_frame (app/models/game_file.py:116-124) uses Python
 * round() — banker's rounding — where this file uses Math.floor for
 * time->frame and Math.round for the halftime boundary n1.
 *
 * TWO FRAME CONVENTIONS EXIST IN LIVE DATA (measured 2026-09-03, LAG@SKC —
 * see KNOWLEDGE.md "Frame clocks"): the mapping below was written for
 * "kickoff=0, gapless" frames, but every censused game's parquets carry
 * VIDEO-CLOCK frames that include the halftime break. Feeding a video-clock
 * H2 frame to the piecewise branch adds the break twice and lands ~15
 * minutes late. `framesIncludeHalftimeGap` selects the correct branch;
 * detectFramesIncludeGap measures which convention a game uses from its own
 * data, so a later pipeline renumbering changes nothing here.
 */
import { servedFps } from '../frameRate'

export interface SyncState {
  videoGameStartTime: number
  videoHalftimeStartTime: number | null
  videoHalftimeEndTime: number | null
  videoFrameAligned: boolean
  /** The game's recorded frame rate, as the server sent it (read through
   *  lib/frameRate servedFps); a page whose game has none builds no
   *  SyncState (the kept exceptions are listed in frameRate.guard.test).
   *  The conversions below throw on a rate that is not a positive number
   *  rather than place a frame at a guessed one. */
  fps: number
  /**
   * True when this game's data frames already contain the halftime break
   * (video-clock). Set from detectFramesIncludeGap, never assumed. Optional
   * so every existing SyncState constructor keeps its exact behavior
   * (undefined = the legacy gapless assumption).
   */
  framesIncludeHalftimeGap?: boolean
  /**
   * Frame-aligned only: each half's steady shift, in seconds, of a
   * variable-frame-rate video's frames from n / fps (game_files
   * .video_offset_h1_s / _h2_s, written by the layer-stack build). Frame n
   * plays at n / fps + the shift of its half; half 2 starts at the
   * halftime-restart mark. Absent is no shift. GameFile applies the same.
   */
  videoOffsetH1?: number | null
  videoOffsetH2?: number | null
  /**
   * Frame-aligned only: a wobbling video's real frame starts,
   * [[frame, seconds], ...] (game_files.video_time_map, written by the
   * layer-stack build). Where set, frame <-> time follows it instead of
   * n / fps + the half's shift. GameFile applies the same; both are held to
   * testdata/time_map_cases.json.
   */
  videoTimeMap?: [number, number][] | null
}

/** The video-sync fields as a payload carries them (video_sync_fields). */
export interface VideoSyncFields {
  video_frame_aligned?: boolean | null
  video_offset_h1_s?: number | null
  video_offset_h2_s?: number | null
  video_time_map?: [number, number][] | null
}

/** A payload's video-sync fields as SyncState fields — one reader for
 *  every builder, so none can drop the time map. The builders keep their
 *  own marks, fps and frame-convention detection. */
export function syncFieldsOf(v: VideoSyncFields | null | undefined): Pick<
  SyncState, 'videoFrameAligned' | 'videoOffsetH1' | 'videoOffsetH2' | 'videoTimeMap'> {
  return {
    videoFrameAligned: !!v?.video_frame_aligned,
    videoOffsetH1: v?.video_offset_h1_s ?? null,
    videoOffsetH2: v?.video_offset_h2_s ?? null,
    videoTimeMap: v?.video_time_map ?? null,
  }
}

/** The state's frame rate, or a throw: GameFile.frame_to_video_time
 *  refuses a game with no recorded rate (FrameRateUnknown) and so does
 *  this, where it used to place every frame at 25. */
function rateOf(s: SyncState): number {
  const fps = servedFps(s.fps)
  if (fps === null) throw new Error(`no frame rate to place a frame with (fps ${String(s.fps)})`)
  return fps
}

/** Frames added before flooring, so a time that IS a frame's start gives
 *  that frame (app/utils/video_time_map FRAME_EPSILON, which says why). */
const FRAME_EPSILON = 1e-3

/** Index of the last element of xs <= x, or -1 (bisect_right - 1). */
function lastAtOrBefore(xs: number[], x: number): number {
  let lo = 0
  let hi = xs.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((xs[mid] ?? Infinity) <= x) lo = mid + 1
    else hi = mid
  }
  return lo - 1
}

/** A map's frame and time columns, built once per map: a conversion runs
 *  per frame against the same map (videoTimeToFrame on every tick). */
const COLUMNS = new WeakMap<[number, number][], { frames: number[]; times: number[] }>()
function columns(points: [number, number][]): { frames: number[]; times: number[] } {
  let c = COLUMNS.get(points)
  if (!c) {
    c = { frames: points.map((p) => p[0]), times: points.map((p) => p[1]) }
    COLUMNS.set(points, c)
  }
  return c
}

/** The point k of a map and the one after it (undefined past the end). */
function around(points: [number, number][], k: number):
  [[number, number], [number, number] | undefined] {
  const a = points[Math.max(0, k)] as [number, number]
  return [a, k < 0 ? undefined : points[k + 1]]
}

/** Video seconds frame n starts at along a time map (frame_to_seconds). */
export function mapFrameToSeconds(points: [number, number][], frame: number, fps: number): number {
  const k = lastAtOrBefore(columns(points).frames, frame)
  const [[na, ta], next] = around(points, k)
  if (k < 0 || next === undefined) return ta + (frame - na) / fps
  const [nb, tb] = next
  return ta + ((tb - ta) * (frame - na)) / (nb - na)
}

/** The frame on screen at video time t along a time map: the last frame
 *  whose start is at or before t (seconds_to_frame). */
export function mapSecondsToFrame(points: [number, number][], t: number, fps: number): number {
  const k = lastAtOrBefore(columns(points).times, t)
  const [[na, ta], next] = around(points, k)
  if (k < 0 || next === undefined) {
    return Math.max(0, Math.floor(na + (t - ta) * fps + FRAME_EPSILON))
  }
  // the last point at or before t, so the next one is strictly after it
  const [nb, tb] = next
  return Math.max(0, Math.floor(na + ((t - ta) * (nb - na)) / (tb - ta) + FRAME_EPSILON))
}

/**
 * Which frame convention does this game's data use? The data's own halftime
 * — the large frame gap the dangerosity series shows (detectHalftime) —
 * ends at ≈ n1 under gapless numbering and at ≈ n1 + the break under
 * video-clock. The two hypotheses differ by the whole ~15-minute break, so
 * the midpoint threshold cannot plausibly misfire. Null input (no detected
 * data halftime, or no sync boundaries) means undetectable: return false
 * and keep the legacy behavior.
 */
export function detectFramesIncludeGap(
  s: SyncState,
  dataHalftimeEndFrame: number | null,
): boolean {
  const vhs = s.videoHalftimeStartTime
  const vhe = s.videoHalftimeEndTime
  if (vhs == null || vhe == null || dataHalftimeEndFrame == null) return false
  const fps = rateOf(s)
  const vgst = s.videoGameStartTime || 0
  const n1 = Math.round((vhs - vgst) * fps)
  const breakFrames = Math.round((vhe - vhs) * fps)
  if (breakFrames <= 0) return false
  return dataHalftimeEndFrame >= n1 + breakFrames / 2
}

/** The state before a game's payload lands. Its 25 is a placeholder,
 *  not a game's rate: a page that has the payload replaces it with the
 *  recorded rate, and a page whose game has none does not place frames
 *  (see the match page's no-frame-rate state). */
export function defaultSyncState(): SyncState {
  return {
    videoGameStartTime: 0.0,
    videoHalftimeStartTime: null,
    videoHalftimeEndTime: null,
    videoFrameAligned: false,
    fps: 25,
  }
}

export function frameToVideoTime(s: SyncState, frame: number): number {
  const fps = rateOf(s)
  const vgst = s.videoGameStartTime || 0
  if (s.videoFrameAligned) {
    if (s.videoTimeMap && s.videoTimeMap.length) return mapFrameToSeconds(s.videoTimeMap, frame, fps)
    // v3: frame N IS video time N/fps, plus its half's shift
    const t = frame / fps
    return t + halfShift(s, t)
  }
  // Video-clock frames already contain the halftime break: linear, both
  // halves. The piecewise branch below would add the break a second time.
  if (s.framesIncludeHalftimeGap) return vgst + frame / fps
  const vhs = s.videoHalftimeStartTime
  const vhe = s.videoHalftimeEndTime
  if (vhs == null || vhe == null) return vgst + frame / fps
  const n1 = Math.round((vhs - vgst) * fps)
  return frame < n1 ? vgst + frame / fps : vhe + (frame - n1) / fps
}

/** The steady shift of the half nominal time t falls in (see SyncState). */
function halfShift(s: SyncState, t: number): number {
  const vhe = s.videoHalftimeEndTime
  if (vhe != null && t >= vhe) return s.videoOffsetH2 ?? 0
  return s.videoOffsetH1 ?? 0
}

export function videoTimeToFrame(s: SyncState, t: number): number {
  const fps = rateOf(s)
  const vgst = s.videoGameStartTime || 0
  if (s.videoFrameAligned) {
    if (s.videoTimeMap && s.videoTimeMap.length) return mapSecondsToFrame(s.videoTimeMap, t, fps)
    // Which half: t holds a half-2 frame when t, less half 2's shift, is
    // past the restart mark (the forward rule compares the frame's
    // unshifted time); then take that half's shift off.
    const shift = halfShift(s, t - (s.videoOffsetH2 ?? 0))
    return Math.max(0, Math.floor((t - shift) * fps))
  }
  // Video-clock frames: linear inverse (see frameToVideoTime).
  if (s.framesIncludeHalftimeGap) return Math.max(0, Math.floor((t - vgst) * fps))
  const vhs = s.videoHalftimeStartTime
  const vhe = s.videoHalftimeEndTime
  if (vhs == null || vhe == null) return Math.max(0, Math.floor((t - vgst) * fps))
  const n1 = Math.round((vhs - vgst) * fps)
  if (t < vhs) return Math.max(0, Math.floor((t - vgst) * fps))
  if (t < vhe) return n1 // inside halftime gap
  return n1 + Math.floor((t - vhe) * fps)
}

/** A deep link's video URL, aimed at the shared moment.
 *
 *  Without the #t media fragment the browser buffers from 0:00, then the
 *  loadedmetadata seek throws that buffer away and buffers again at the
 *  target. The fragment aims the FIRST range request; the exact-frame
 *  seek still runs afterwards and is nearly free because the buffer
 *  already starts there. Fragments are never sent to the server, so a
 *  presigned URL's signature is untouched.
 */
export function withStartFragment(
  url: string, s: SyncState, frame: number | null | undefined,
): string {
  if (frame == null || !Number.isFinite(frame) || frame <= 0) return url
  const t = frameToVideoTime(s, frame)
  if (!(t > 0)) return url
  const base = url.split('#')[0]
  return `${base}#t=${t.toFixed(3)}`
}

/** One-frame arrow-key step: an anchored SEQUENCE, not a re-derivation.
 *
 *  The step must not trust the element's read-back time when it sits
 *  where OUR OWN last press put it: after a seek, browsers may report
 *  currentTime as the presented media frame's timestamp — at or slightly
 *  below the requested time — and re-deriving the frame from that value
 *  recomputes the same target forever. The symptom is exact: the arrow
 *  works ONCE from any organic position, then never again until play or
 *  a scrub relocates the playhead (caught live on dev, 2026-09-25, with
 *  #400's float fix already serving — a different pin, same posture).
 *
 *  So the caller keeps the anchor this function returns and hands it
 *  back on the next press. When the element still sits within the
 *  snap-back tolerance of the anchor, the sequence continues from the
 *  anchor's FRAME — presses walk 1, 2, 3 in game frames whatever the
 *  element echoes. Anything that moves the playhead for real (play, a
 *  scrub, a moment jump) lands outside the tolerance and the sequence
 *  re-derives from the element, exactly as before.
 *
 *  Steps land on frame boundaries through the sync mapping (a mid-frame
 *  start snaps onto the grid; the halftime interval is crossed whole —
 *  frame n1 IS the collapsed break), and the derivation keeps #400's
 *  micro-frame tolerance: (n/fps)*fps rounds below n for many n, and a
 *  plain floor pinned the playhead at those boundaries. duration may be
 *  NaN before metadata; the clamp must not eat the step.
 */
export type StepAnchor = { frame: number; time: number }

/** One media frame at 12.5fps — longer than any real camera's frame
 *  period, far shorter than a deliberate human scrub. A read-back inside
 *  this window of the anchor is the element echoing our own seek, not
 *  the user moving. */
const SNAPBACK_TOLERANCE_S = 0.08

export function stepFrameSequence(
  s: SyncState, t: number, dir: 1 | -1, duration: number,
  last: StepAnchor | null,
): StepAnchor {
  const fps = rateOf(s)
  const continuing =
    last != null && Math.abs(t - last.time) < SNAPBACK_TOLERANCE_S
  const here = continuing
    ? last.frame
    : videoTimeToFrame(s, t + 1 / (fps * 1e6))
  const frame = Math.max(0, here + dir)
  const max = Number.isFinite(duration) ? duration : Infinity
  const time = Math.min(Math.max(frameToVideoTime(s, frame), 0), max)
  return { frame, time }
}

/** Format game time from frame ("MM:SS", both padded) — template formatGameTime. */
export function formatGameTime(frame: number, fps: number): string {
  const seconds = Math.floor(frame / fps)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/** Parse "MM:SS" back to a frame (mark-a-moment) — template timeToFrame. */
export function timeToFrame(timeStr: string, fps: number): number | null {
  if (!timeStr || timeStr === '--:--') return null
  // Destructured, not indexed: the length check proves both exist and the
  // compiler cannot see that through parts[0]. Destructuring lets it.
  const [minsStr, secsStr] = timeStr.trim().split(':')
  if (minsStr === undefined || secsStr === undefined) return null
  const mins = parseInt(minsStr, 10)
  const secs = parseInt(secsStr, 10)
  if (isNaN(mins) || isNaN(secs)) return null
  return (mins * 60 + secs) * fps
}

/** Format "M:SS" (manual-moment end times) — template frameToTime. */
export function frameToTime(frame: number, fps: number): string {
  const totalSeconds = Math.floor(frame / fps)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
